// src/pages/Automacao.js — Página /automacao (regras de automação)
//
// Tem 1 automação (Resposta automática de primeiro contato). Os 3 cards
// "Em desenvolvimento" (agendamento de templates, disparo por etiqueta e
// sugestão via IA) saíram em out/26: os três já existem — Disparos
// agendados no CRM, filtro por etiqueta no envio em lote e a aba IA
// Assistida. Alimentada por:
//   GET    /automations/welcome-first-contact           → config + chips
//   PATCH  /automations/welcome-first-contact            → master on/off
//   PATCH  /automations/welcome-first-contact/devices/X → chip config
//   POST   /automations/welcome-first-contact/test/X    → teste manual
//   GET    /automations/welcome-first-contact/logs      → últimos disparos
//   GET    /messages/templates/{device_id}              → templates APPROVED
//
// Trigger real acontece no backend (webhook.py chama fire_welcome_if_applicable).
// Frontend só configura + mostra.

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Tooltip, Chip, MenuItem, Select, ThemeProvider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  FiZap, FiCheck, FiPlay, FiLock, FiAlertTriangle, FiImage, FiFileText,
  FiMessageSquare, FiSettings, FiCpu, FiChevronDown, FiChevronUp,
} from 'react-icons/fi';
import AppSidebar from '../components/AppSidebar';
import TopBar from '../components/TopBar';
import BloqueioTeste from '../components/assinatura/BloqueioTeste';
import api from '../services/Api';
import AutomacaoMessages from '../components/AutomacaoMessages';
import AutomacaoIA from '../components/AutomacaoIA';
import { C, temaMuiClaro, temaMuiEscuro } from '../components/automacao/tema';

const SYNE = "'Syne', system-ui, sans-serif";
const OUTFIT = "'Outfit', system-ui, sans-serif";

// Mesma escolha de tema do chat, da Agenda, dos Formulários e do CRM.
const CHAVE_TEMA = 'cetto:chat-theme';

// Últimos disparos (out/26): a lista de 20 empurrava o resto da página pra
// baixo. Mostra os 3 mais recentes e cresce de 5 em 5 no "Ver mais".
const DISPAROS_INICIAIS = 3;
const PASSO_VER_MAIS = 5;

// ─── Sub-componentes ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, subColor }) {
  return (
    <Box sx={{
      bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
      p: '16px 18px', boxShadow: C.sombra,
    }}>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.62rem', fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: C.text3, mb: 0.6,
      }}>{label}</Typography>
      <Typography sx={{
        fontFamily: OUTFIT, fontSize: '1.7rem', fontWeight: 800,
        letterSpacing: '-1px', lineHeight: 1, color: C.text, mb: 0.4,
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.62rem', color: subColor || C.text3,
      }}>
        {sub}
      </Typography>
    </Box>
  );
}

function ToggleSwitch({ on, onChange, size = 'md' }) {
  const w = size === 'sm' ? 32 : 40;
  const h = size === 'sm' ? 18 : 22;
  const knob = size === 'sm' ? 10 : 14;
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); onChange && onChange(!on); }}
      sx={{
        width: w, height: h, borderRadius: h / 2, cursor: 'pointer',
        border: `1.5px solid ${on ? C.green : C.bdr2}`,
        bgcolor: on ? C.green : C.inset,
        position: 'relative', flexShrink: 0,
        transition: 'all .2s',
      }}
    >
      <Box sx={{
        position: 'absolute',
        top: 2, left: on ? w - knob - 4 : 2,
        width: knob, height: knob, borderRadius: '50%',
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left .2s',
      }} />
    </Box>
  );
}

function CondItem({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.2} sx={{
      p: '9px 12px', borderRadius: '6px',
      bgcolor: C.surf, border: `1px solid ${C.bdr}`, mb: 1,
    }}>
      <Box sx={{
        width: 18, height: 18, borderRadius: '5px',
        bgcolor: C.greenBg, border: `1.5px solid ${C.greenB}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: C.greenD, flexShrink: 0,
      }}>
        <FiCheck size={11} strokeWidth={3} />
      </Box>
      <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text }}>
        {children}
      </Typography>
    </Stack>
  );
}

function InfoBlock({ label, value, variant = 'default' }) {
  const v = variant === 'warn'
    ? { bg: C.amberBg, border: C.amberB, color: C.amberD }
    : { bg: C.surf, border: C.bdr, color: C.text };
  return (
    <Box sx={{
      p: '10px 12px', borderRadius: '6px',
      bgcolor: v.bg, border: `1px solid ${v.border}`,
    }}>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.6rem', fontWeight: 700,
        color: variant === 'warn' ? C.amberD : C.text3,
        letterSpacing: '0.05em', mb: 0.3,
      }}>{label}</Typography>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.75rem', fontWeight: 600, color: v.color,
      }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────

export default function Automacao() {
  const token = localStorage.getItem('token');
  // AppSidebar precisa do user (name + plan) pro rodapé "Marcelo Ximenes · starter".
  // Mesmo pattern usado em AdminChips.
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Tema (o mesmo do chat) ────────────────────────────────────────────
  // `data-theme` no <html> enquanto a página está montada: é o que troca os
  // `--ch-*`/`--au-*` de components/automacao/tema.js, inclusive dentro de
  // diálogos e menus (portal no <body>).
  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem(CHAVE_TEMA) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  useLayoutEffect(() => {
    const el = document.documentElement;
    if (tema === 'dark') el.setAttribute('data-theme', 'dark'); else el.removeAttribute('data-theme');
    return () => el.removeAttribute('data-theme');
  }, [tema]);
  const trocarTema = useCallback(() => setTema((t) => {
    const n = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(CHAVE_TEMA, n); } catch { /* storage indisponível */ }
    return n;
  }), []);

  // Tab: 'management' (gerenciamento) | 'messages' (biblioteca de mensagens)
  const [tab, setTab] = useState('management');

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);        // { master_enabled, chips: [...], available_messages: [...], ... }
  const [logs, setLogs] = useState([]);
  const [disparosVistos, setDisparosVistos] = useState(DISPAROS_INICIAIS);
  const [savingMaster, setSavingMaster] = useState(false);
  const [savingChip, setSavingChip] = useState(null);   // device_id enquanto salva
  const [testOpen, setTestOpen] = useState(null);       // {device} ou null
  const [testPhone, setTestPhone] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testFeedback, setTestFeedback] = useState(null);
  const [toast, setToast] = useState(null);             // {kind, msg}

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ─── Fetch config + logs em paralelo ────────────────────────────────────
  // available_messages já vem no config (biblioteca da company) — não
  // precisa fetchar templates Meta separado (não usamos template pago aqui).
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, lg] = await Promise.all([
        api.get('/automations/welcome-first-contact', { headers }),
        api.get('/automations/welcome-first-contact/logs', { headers, params: { limit: 20 } }),
      ]);
      setConfig(cfg.data);
      setLogs(lg.data || []);
    } catch (err) {
      setToast({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao carregar automação.' });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { refresh(); }, [refresh]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const toggleMaster = async () => {
    if (!config) return;
    const next = !config.master_enabled;
    setSavingMaster(true);
    // Optimistic
    setConfig(c => ({ ...c, master_enabled: next }));
    try {
      await api.patch('/automations/welcome-first-contact', { enabled: next }, { headers });
    } catch (err) {
      setConfig(c => ({ ...c, master_enabled: !next }));
      setToast({ kind: 'error', msg: 'Não deu pra atualizar. Tenta de novo.' });
    } finally {
      setSavingMaster(false);
    }
  };

  const saveChip = async (deviceId, patch) => {
    setSavingChip(deviceId);
    try {
      await api.patch(`/automations/welcome-first-contact/devices/${deviceId}`, patch, { headers });
      // Atualiza local
      setConfig(c => ({
        ...c,
        chips: c.chips.map(x => x.device_id === deviceId ? { ...x, ...patch } : x),
      }));
    } catch (err) {
      setToast({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao salvar.' });
    } finally {
      setSavingChip(null);
    }
  };

  const runTest = async () => {
    if (!testOpen?.device || !testPhone.trim()) return;
    setTestBusy(true);
    setTestFeedback(null);
    try {
      const res = await api.post(
        `/automations/welcome-first-contact/test/${testOpen.device.device_id}`,
        { phone_number: testPhone.trim() },
        { headers },
      );
      setTestFeedback({ kind: 'ok', msg: `Mensagem disparada com sucesso (${res.data.message_name || 'ok'}).` });
      setTimeout(() => { setTestOpen(null); setTestPhone(''); setTestFeedback(null); }, 2200);
      // Recarrega logs pra mostrar o teste (se o webhook de status já entrou).
      setTimeout(refresh, 1500);
    } catch (err) {
      setTestFeedback({ kind: 'error', msg: err.response?.data?.detail || 'Falha no disparo.' });
    } finally {
      setTestBusy(false);
    }
  };

  const activeCount = config?.master_enabled ? 1 : 0;
  const configuredChips = (config?.chips || []).filter(c => c.enabled && c.template_name).length;
  const totalChips = (config?.chips || []).length;
  const deliveryRate = useMemo(() => {
    if (!logs.length) return '—';
    const ok = logs.filter(l => l.status === 'OK').length;
    return `${Math.round((ok / logs.length) * 100)}%`;
  }, [logs]);

  // O backend já devolve do mais novo pro mais velho; a tela ordena de novo
  // porque é ela que promete "mais recentes primeiro" no cabeçalho.
  const disparos = useMemo(
    () => [...logs].sort((a, b) => new Date(b.fired_at) - new Date(a.fired_at)),
    [logs],
  );
  const disparosNaTela = disparos.slice(0, disparosVistos);
  const disparosFaltando = disparos.length - disparosNaTela.length;

  return (
    // O ThemeProvider fica sempre (só o tema muda): trocar a árvore ao
    // alternar claro/escuro remontaria a página e perderia a aba aberta.
    <ThemeProvider theme={tema === 'dark' ? temaMuiEscuro : temaMuiClaro}>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: C.bg, color: C.text }}>
      <AppSidebar user={user} />
      {/* Sidebar é position:fixed → precisa reservar o espaço com ml=sidebar-width
          (mesmo pattern das outras páginas). Sem isso, o conteúdo fica atrás. */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
        ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
        transition: 'margin-left 0.25s ease',
      }}>
        <TopBar crumbBase="Cetto System" crumbCur="Automações" themeMode={tema} onToggleTheme={trocarTema} />

        <BloqueioTeste pagina="automacao" sx={{
          overflowY: 'auto', p: { xs: 2, md: 4 },
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: C.bdr2, borderRadius: 4 },
        }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.2} mb={0.8}>
              <Typography sx={{
                fontFamily: SYNE, fontSize: { xs: '1.4rem', md: '1.6rem' },
                fontWeight: 800, letterSpacing: '-0.5px', color: C.text,
              }}>
                Automações
              </Typography>
              <Chip
                label="Novo"
                size="small"
                sx={{
                  bgcolor: C.greenBg, color: C.greenD, border: `1px solid ${C.greenB}`,
                  fontFamily: SYNE, fontWeight: 700, fontSize: '0.6rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  height: 20, borderRadius: '4px',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Stack>
            <Typography sx={{
              fontFamily: SYNE, fontSize: '0.82rem', color: C.text2,
              maxWidth: 640, lineHeight: 1.6,
            }}>
              Configure regras automáticas que atuam sobre as conversas dos seus chips.
              Cada automação usa a infraestrutura de webhook já existente — sem código adicional.
            </Typography>
          </Box>

          {/* Tabs bar */}
          <Box sx={{
            display: 'flex', gap: 0.5, borderBottom: `1px solid ${C.bdr}`, mb: 3,
          }}>
            {[
              { id: 'management', label: 'Gerenciamento', icon: <FiSettings size={14} /> },
              { id: 'messages',   label: 'Mensagens personalizadas', icon: <FiMessageSquare size={14} /> },
              { id: 'ai',         label: 'IA Assistida', icon: <FiCpu size={14} />, badge: 'NOVO' },
            ].map(t => {
              const active = tab === t.id;
              return (
                <Box
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.8,
                    px: 2, py: 1.2, cursor: 'pointer',
                    borderBottom: `2px solid ${active ? C.green : 'transparent'}`,
                    color: active ? C.text : C.text2, fontFamily: SYNE, fontWeight: active ? 700 : 500,
                    fontSize: '0.82rem', mb: '-1px',
                    transition: 'all .15s',
                    '&:hover': { color: C.text },
                  }}
                >
                  {t.icon}
                  {t.label}
                  {t.badge && (
                    <Box component="span" sx={{
                      ml: 0.4, fontFamily: SYNE, fontSize: '0.5rem', fontWeight: 800,
                      letterSpacing: '0.08em', px: 0.7, py: '1px',
                      borderRadius: '3px',
                      bgcolor: C.purpBg, color: C.purple,
                      border: `1px solid ${C.purpB}`,
                    }}>
                      {t.badge}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Aba: Mensagens personalizadas — renderiza componente separado */}
          {tab === 'messages' && (
            <AutomacaoMessages
              onLibraryChanged={refresh}
              headers={headers}
              onToast={setToast}
            />
          )}

          {/* Aba: IA Assistida — mock-only por enquanto (backend BYOK vem
              em PR separada depois da UX ser validada). Não persiste nada
              nem consome API; toda a interação é local pra design review. */}
          {tab === 'ai' && (
            <AutomacaoIA onToast={setToast} />
          )}

          {/* Aba: Gerenciamento — resto do JSX abaixo até o final */}
          {tab === 'management' && <>

          {/* Stats strip */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1.5, mb: 3,
          }}>
            <StatCard
              label="Automações ativas"
              value={<>{activeCount}<Box component="span" sx={{ color: C.text3, fontSize: '0.9rem', ml: 0.5 }}>/1</Box></>}
              sub={activeCount === 1 ? 'Welcome ligada' : 'Nenhuma ligada'}
            />
            <StatCard
              label="Disparos (24h)"
              value={<Box component="span" sx={{ color: C.green }}>{config?.fires_last_24h ?? 0}</Box>}
              sub={config?.fires_last_24h > 0 ? '↑ enviando' : 'Aguardando primeira msg'}
              subColor={config?.fires_last_24h > 0 ? C.greenD : C.text3}
            />
            <StatCard
              label="Taxa de entrega"
              value={deliveryRate}
              sub={logs.length ? `${logs.filter(l => l.status === 'OK').length}/${logs.length} entregues` : '—'}
              subColor={deliveryRate === '100%' ? C.greenD : C.text3}
            />
            <StatCard
              label="Chips configurados"
              value={configuredChips}
              sub={`de ${totalChips} chip${totalChips !== 1 ? 's' : ''} ativo${totalChips !== 1 ? 's' : ''}`}
            />
          </Box>

          {/* Seção: Disponível agora */}
          <SectionTitle title="Disponível agora" count={1} />

          {/* Card: Welcome first contact — expandido por padrão */}
          <Box sx={{
            bgcolor: C.card, border: `1px solid ${config?.master_enabled ? C.greenB : C.bdr}`,
            borderRadius: '10px', mb: 1.5,
            boxShadow: C.sombra,
            overflow: 'hidden',
          }}>
            {/* Header do card */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ p: 2.2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
                bgcolor: C.greenBg, border: `1px solid ${C.greenB}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FiZap size={20} color={C.greenD} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
                  <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.9rem', color: C.text }}>
                    Resposta automática de primeiro contato
                  </Typography>
                  <Chip
                    label={config?.master_enabled ? '● Ativa' : '○ Inativa'}
                    size="small"
                    sx={{
                      height: 20, borderRadius: '20px',
                      bgcolor: config?.master_enabled ? C.greenBg : C.inset,
                      color: config?.master_enabled ? C.greenD : C.text3,
                      border: `1px solid ${config?.master_enabled ? C.greenB : C.bdr}`,
                      fontFamily: SYNE, fontWeight: 700, fontSize: '0.58rem',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Stack>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text2, lineHeight: 1.55 }}>
                  Quando um número envia a primeira mensagem para um chip, o template
                  de boas-vindas configurado é disparado automaticamente.
                </Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" alignItems="center">
                  <Chip label="Webhook" size="small" sx={pillSx(C.blueBg, C.blueD, C.blueB)} />
                  <Chip label="Template Meta" size="small" sx={pillSx(C.inset, C.text3, C.bdr)} />
                  <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.7rem', color: C.text3 }}>
                    <b style={{ color: C.greenD }}>{config?.fires_last_24h ?? 0}</b> disparos (24h)
                  </Typography>
                </Stack>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.2} sx={{ flexShrink: 0 }}>
                <ToggleSwitch on={!!config?.master_enabled} onChange={toggleMaster} />
                <Typography sx={{
                  fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700,
                  color: config?.master_enabled ? C.greenD : C.text2, minWidth: 44,
                }}>
                  {config?.master_enabled ? 'Ativa' : 'Inativa'}
                </Typography>
              </Stack>
            </Stack>

            {/* Config expandido */}
            <Box sx={{ borderTop: `1px solid ${C.bdr}`, bgcolor: C.surf, p: 2.5 }}>
              {/* Grid: Condições + Ação */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2, mb: 2.5,
              }}>
                {/* Condições */}
                <Box sx={{ bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px', p: 2 }}>
                  <SectionSubtitle icon="📋" text="Condições para disparar" />
                  <CondItem>Primeira mensagem de um <b>número novo</b> no chip</CondItem>
                  <CondItem>Chip com <b>template de boas-vindas</b> configurado abaixo</CondItem>
                  <CondItem>Health Score do chip <b>≥ {config?.health_lock_threshold ?? 80}%</b> (proteção anti-flag)</CondItem>
                  <Box sx={{ mt: 1.5, p: '10px 12px', bgcolor: C.greenBg, border: `1px solid ${C.greenB}`, borderRadius: '6px' }}>
                    <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', fontWeight: 700, color: C.greenD, mb: 0.3 }}>
                      Se todas as condições forem verdadeiras
                    </Typography>
                    <Typography sx={{ fontFamily: SYNE, fontSize: '0.72rem', color: C.greenD }}>
                      → O template é disparado automaticamente para o número.
                    </Typography>
                  </Box>
                </Box>

                {/* Ação */}
                <Box sx={{ bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px', p: 2 }}>
                  <SectionSubtitle icon="🎯" text="Ação executada" />
                  <Stack spacing={1.2}>
                    <InfoBlock label="TIPO DE AÇÃO" value="Envio de template aprovado Meta" />
                    <InfoBlock
                      label="JANELA DE DEDUPLICAÇÃO"
                      value={`${config?.dedup_window_hours ?? 24} horas — mesmo número não recebe duas vezes`}
                    />
                    <InfoBlock
                      label="⚠ APENAS TEMPLATES APROVADOS"
                      value="Somente templates com status 'Approved' pela Meta aparecem nas opções abaixo."
                      variant="warn"
                    />
                  </Stack>
                </Box>
              </Box>

              {/* Tabela de chips */}
              <Box sx={{ bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px', p: 2, mb: 2 }}>
                <SectionSubtitle icon="📱" text="Template por chip" />

                {loading ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <CircularProgress size={22} sx={{ color: C.green }} />
                  </Box>
                ) : (config?.chips || []).length === 0 ? (
                  <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text3, textAlign: 'center', py: 3 }}>
                    Você ainda não tem chips ativos. Conecte um em <b>/admin/chips</b>.
                  </Typography>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.4fr 2fr 90px' },
                    gap: 1.2, alignItems: 'stretch',
                  }}>
                    {/* Header */}
                    <Box sx={colHeaderSx}>Chip</Box>
                    <Box sx={{ ...colHeaderSx, display: { xs: 'none', md: 'block' } }}>Template de boas-vindas</Box>
                    <Box sx={{ ...colHeaderSx, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>Ativo</Box>

                    {(config?.chips || []).map((chip, idx) => {
                      const locked = chip.health_locked;
                      const availableMessages = config?.available_messages || [];
                      const busy = savingChip === chip.device_id;
                      const dotColor = locked ? C.amber : C.green;
                      return (
                        <Box key={chip.device_id} sx={{ display: 'contents' }}>
                          {/* col chip */}
                          <Box sx={{ py: 1, ...(idx > 0 ? { borderTop: `1px solid ${C.bdr}` } : {}) }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{
                                width: 7, height: 7, borderRadius: '50%', bgcolor: dotColor,
                                boxShadow: locked ? 'none' : `0 0 4px ${C.greenGlow}`, flexShrink: 0,
                              }} />
                              <Box>
                                <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.78rem', fontWeight: 600, color: C.text }}>
                                  {chip.phone_number}
                                </Typography>
                                <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', color: C.text3 }}>
                                  {chip.name}{locked ? ' · atenção' : ''}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>

                          {/* col template */}
                          <Box sx={{
                            py: 1, display: 'flex', alignItems: 'center', gap: 1,
                            ...(idx > 0 ? { borderTop: { md: `1px solid ${C.bdr}` } } : {}),
                          }}>
                            <Select
                              value={chip.custom_message_id || ''}
                              onChange={(e) => saveChip(chip.device_id, { custom_message_id: e.target.value })}
                              disabled={locked || busy}
                              displayEmpty
                              size="small"
                              fullWidth
                              sx={{
                                fontFamily: SYNE, fontSize: '0.75rem', bgcolor: C.surf,
                                borderRadius: '6px', height: 34,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: C.bdr },
                                '&.Mui-disabled': { opacity: 0.5 },
                              }}
                              renderValue={(v) => {
                                if (!v) {
                                  return <Box component="span" sx={{ color: C.text3 }}>
                                    {locked ? '— chip com atenção —' : 'Selecionar mensagem…'}
                                  </Box>;
                                }
                                const m = availableMessages.find(x => x.id === v);
                                return (
                                  <Stack direction="row" alignItems="center" spacing={0.8}>
                                    {m?.has_media && (m.media_type === 'IMAGE'
                                      ? <FiImage size={12} color={C.blueD} />
                                      : <FiFileText size={12} color={C.purple} />
                                    )}
                                    <Box component="span">{m?.name || '(mensagem removida)'}</Box>
                                  </Stack>
                                );
                              }}
                            >
                              <MenuItem value="" sx={{ fontFamily: SYNE, fontSize: '0.75rem', fontStyle: 'italic', color: C.text3 }}>
                                — Sem mensagem —
                              </MenuItem>
                              {availableMessages.map(m => (
                                <MenuItem key={m.id} value={m.id} sx={{ fontFamily: SYNE, fontSize: '0.75rem', display: 'block', py: 0.8 }}>
                                  <Stack direction="row" alignItems="center" spacing={0.8} mb={0.2}>
                                    {m.has_media && (m.media_type === 'IMAGE'
                                      ? <FiImage size={12} color={C.blueD} />
                                      : <FiFileText size={12} color={C.purple} />
                                    )}
                                    <Box component="span" sx={{ fontWeight: 600 }}>{m.name}</Box>
                                  </Stack>
                                  {m.preview && (
                                    <Typography sx={{
                                      fontFamily: SYNE, fontSize: '0.66rem', color: C.text3,
                                      lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden',
                                      textOverflow: 'ellipsis', maxWidth: 320,
                                    }}>
                                      {m.preview}
                                    </Typography>
                                  )}
                                </MenuItem>
                              ))}
                              {availableMessages.length === 0 && (
                                <MenuItem disabled sx={{ fontFamily: SYNE, fontSize: '0.72rem', color: C.text3 }}>
                                  Nenhuma mensagem criada — vá na aba "Mensagens personalizadas"
                                </MenuItem>
                              )}
                            </Select>
                            {chip.custom_message_id && !locked && (
                              <Tooltip title="Testar disparo">
                                <IconButton
                                  size="small"
                                  onClick={() => { setTestOpen({ device: chip }); setTestPhone(''); setTestFeedback(null); }}
                                  sx={{
                                    border: `1px solid ${C.blueB}`, bgcolor: C.blueBg, color: C.blueD,
                                    borderRadius: '6px', width: 30, height: 30,
                                    '&:hover': { bgcolor: C.blueB },
                                  }}
                                >
                                  <FiPlay size={12} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {/* col toggle */}
                          <Box sx={{
                            py: 1, display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'center' }, gap: 1,
                            ...(idx > 0 ? { borderTop: { md: `1px solid ${C.bdr}` } } : {}),
                          }}>
                            {locked ? (
                              <Tooltip title={`Chip com health < ${config?.health_lock_threshold ?? 80}% — automação bloqueada`}>
                                <Chip
                                  size="small"
                                  icon={<FiLock size={10} />}
                                  label="Bloqueado"
                                  sx={{
                                    bgcolor: C.amberBg, color: C.amberD, border: `1px solid ${C.amberB}`,
                                    fontFamily: SYNE, fontSize: '0.6rem', fontWeight: 700, height: 22,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <ToggleSwitch
                                size="sm"
                                on={chip.enabled}
                                onChange={(v) => saveChip(chip.device_id, { enabled: v })}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* Footer */}
              <Stack direction="row" alignItems="center" spacing={1.2} sx={{ pt: 1, borderTop: `1px solid ${C.bdr}` }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.green }} />
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.72rem', color: C.text3, flex: 1 }}>
                  Alterações são salvas automaticamente ao mudar o dropdown ou o toggle.
                </Typography>
              </Stack>
            </Box>
          </Box>

          {/* Log de disparos */}
          <SectionTitle title="Últimos disparos" tag={logs.length === 0 ? 'Hoje' : `${logs.length} recentes`} mt={2.5} />
          <Box sx={{
            bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
            overflow: 'hidden', boxShadow: C.sombra, mb: 3.5,
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
              p: '12px 18px', bgcolor: C.surf, borderBottom: `1px solid ${C.bdr}`,
            }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FiZap size={14} color={C.greenD} />
                <Typography sx={{
                  fontFamily: SYNE, fontSize: '0.7rem', fontWeight: 700,
                  color: C.text2, letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>
                  Resposta automática de primeiro contato
                </Typography>
              </Stack>
              {logs.length > 0 && (
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.7rem', color: C.text3, whiteSpace: 'nowrap' }}>
                  Mais recentes primeiro
                </Typography>
              )}
            </Stack>

            {logs.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text3 }}>
                  Nenhum disparo ainda — assim que a primeira mensagem chegar a um número novo, aparece aqui.
                </Typography>
              </Box>
            ) : (
              <Box>
                {/* Header row. No celular a coluna Template sai: o número e o
                    status são o que se confere de relance. */}
                <Box sx={{ ...linhaDisparoSx, borderBottom: `1px solid ${C.bdr}` }}>
                  {['Hora', 'Número', 'Template', 'Status'].map((h, i) => (
                    <Typography key={h} sx={{
                      fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
                      color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase',
                      justifySelf: i === 3 ? 'end' : 'start',
                      display: i === 2 ? { xs: 'none', sm: 'block' } : 'block',
                    }}>{h}</Typography>
                  ))}
                </Box>
                {disparosNaTela.map((l, idx) => (
                  <Box key={l.id} sx={{
                    ...linhaDisparoSx, alignItems: 'center',
                    borderBottom: idx < disparosNaTela.length - 1 ? `1px solid ${C.bdr}` : 'none',
                    '&:hover': { bgcolor: C.surf },
                  }}>
                    <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.68rem', color: C.text3 }}>
                      {formatTime(l.fired_at)}
                    </Typography>
                    <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.72rem', fontWeight: 600, color: C.text, wordBreak: 'break-all' }}>
                      {l.phone_number}
                    </Typography>
                    <Typography sx={{
                      fontFamily: SYNE, fontSize: '0.72rem', color: C.text2, wordBreak: 'break-all',
                      display: { xs: 'none', sm: 'block' },
                    }}>
                      {l.template_name || '—'}
                    </Typography>
                    <Chip
                      size="small"
                      icon={l.status === 'OK' ? <FiCheck size={10} /> : <FiAlertTriangle size={10} />}
                      label={l.status === 'OK' ? 'Entregue' : 'Falhou'}
                      sx={{
                        justifySelf: 'end', height: 20, borderRadius: '20px',
                        bgcolor: l.status === 'OK' ? C.greenBg : C.redBg,
                        color: l.status === 'OK' ? C.greenD : C.redD,
                        border: `1px solid ${l.status === 'OK' ? C.greenB : C.redB}`,
                        fontFamily: SYNE, fontSize: '0.6rem', fontWeight: 700,
                        '& .MuiChip-label': { px: 0.7 },
                      }}
                    />
                  </Box>
                ))}

                {/* Rodapé só quando há mais do que cabe de início. Um botão
                    só, que troca de "Ver mais" pra "Mostrar menos": o foco
                    do teclado fica nele em vez de cair no <body>. */}
                {disparos.length > DISPAROS_INICIAIS && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{
                    p: '10px 18px', borderTop: `1px solid ${C.bdr}`, bgcolor: C.surf,
                  }}>
                    <Typography aria-live="polite" sx={{ fontFamily: OUTFIT, fontSize: '0.7rem', color: C.text3 }}>
                      Mostrando <Box component="b" sx={{ color: C.text2, fontWeight: 600 }}>{disparosNaTela.length}</Box>
                      {' '}de {disparos.length} disparos
                    </Typography>
                    <Button
                      onClick={() => setDisparosVistos(v => (disparosFaltando > 0 ? v + PASSO_VER_MAIS : DISPAROS_INICIAIS))}
                      endIcon={disparosFaltando > 0 ? <FiChevronDown size={13} /> : <FiChevronUp size={13} />}
                      sx={{
                        fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
                        px: 1.5, py: 0.5, borderRadius: '6px', bgcolor: C.card, flexShrink: 0,
                        color: disparosFaltando > 0 ? C.greenD : C.text2,
                        border: `1px solid ${disparosFaltando > 0 ? C.greenB : C.bdr2}`,
                        '&:hover': {
                          bgcolor: disparosFaltando > 0 ? C.greenBg : C.inset,
                          borderColor: disparosFaltando > 0 ? C.green : C.bdr2,
                        },
                        '& .MuiButton-endIcon': { ml: 0.6 },
                      }}
                    >
                      {disparosFaltando > 0
                        ? `Ver mais ${Math.min(PASSO_VER_MAIS, disparosFaltando)}`
                        : 'Mostrar menos'}
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ height: 32 }} />
          </>}
        </BloqueioTeste>
      </Box>

      {/* Toast simples */}
      {toast && (
        <Alert
          severity={toast.kind === 'error' ? 'error' : 'success'}
          onClose={() => setToast(null)}
          sx={{
            position: 'fixed', bottom: 20, right: 20, zIndex: 1500,
            boxShadow: C.sombraAviso, fontFamily: SYNE,
          }}
        >
          {toast.msg}
        </Alert>
      )}

      {/* Dialog: Testar disparo */}
      <Dialog
        open={Boolean(testOpen)}
        onClose={() => !testBusy && setTestOpen(null)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiPlay size={16} color={C.green} />
          Testar disparo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text2, mb: 2 }}>
            Vai disparar a mensagem <b>{
              (config?.available_messages || []).find(m => m.id === testOpen?.device?.custom_message_id)?.name || '(sem nome)'
            }</b> pelo chip <b>{testOpen?.device?.phone_number}</b> — <u>ignorando dedup 24h</u> (é teste manual).
          </Typography>
          {testFeedback && (
            <Alert severity={testFeedback.kind === 'ok' ? 'success' : 'error'} sx={{ mb: 2 }}>
              {testFeedback.msg}
            </Alert>
          )}
          <TextField
            label="Número de destino (com DDI)"
            placeholder="Ex: 5521999998888"
            value={testPhone}
            onChange={e => setTestPhone(e.target.value)}
            fullWidth size="small"
            autoFocus
            disabled={testBusy}
            helperText="Sem +, apenas dígitos."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setTestOpen(null)}
            disabled={testBusy}
            sx={{ textTransform: 'none', fontFamily: SYNE, color: C.text2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={runTest}
            disabled={testBusy || !testPhone.trim()}
            variant="contained"
            startIcon={testBusy ? <CircularProgress size={12} /> : <FiPlay size={12} />}
            sx={{
              bgcolor: C.green, color: C.sobreCor, fontFamily: SYNE, fontWeight: 800,
              textTransform: 'none', borderRadius: 2,
              '&:hover': { bgcolor: C.greenHover },
            }}
          >
            {testBusy ? 'Disparando…' : 'Disparar teste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
}

// ─── Estilos e helpers menores ─────────────────────────────────────────────

const pillSx = (bg, color, border) => ({
  height: 20, borderRadius: '20px',
  bgcolor: bg, color, border: `1px solid ${border}`,
  fontFamily: SYNE, fontWeight: 700, fontSize: '0.58rem',
  letterSpacing: '0.06em', textTransform: 'uppercase',
  '& .MuiChip-label': { px: 1 },
});

// Colunas da lista de disparos (cabeçalho e linhas): Hora, Número, Template
// e Status; no celular, sem Template.
const linhaDisparoSx = {
  display: 'grid', gap: 1.5, p: '10px 18px',
  gridTemplateColumns: { xs: '48px 1fr 84px', sm: '90px 1fr 1fr 90px' },
};

const colHeaderSx = {
  fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
  color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase',
  pb: 1, borderBottom: `1px solid ${C.bdr}`,
};

function SectionTitle({ title, count, tag, mt }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.5, mt }}>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700,
        color: C.text2, letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>{title}</Typography>
      {(count !== undefined || tag) && (
        <Box sx={{
          fontFamily: OUTFIT, fontSize: '0.6rem', fontWeight: 600,
          padding: '2px 8px', borderRadius: '20px',
          bgcolor: C.inset, color: C.text3, border: `1px solid ${C.bdr}`,
        }}>
          {count !== undefined ? count : tag}
        </Box>
      )}
      <Box sx={{ flex: 1, height: '1px', bgcolor: C.bdr }} />
    </Stack>
  );
}

function SectionSubtitle({ icon, text }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.8} mb={1.5}>
      <Box component="span" sx={{ fontSize: '0.9rem' }}>{icon}</Box>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.68rem', fontWeight: 700,
        color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        {text}
      </Typography>
    </Stack>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '—';
  }
}
